import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { authHeader, clearToken } from "../../auth";
import RaceHeaderForm, { RaceTypeChooser } from "./RaceHeaderForm";
import RaceSheetForm, { EMPTY_SHEET } from "./RaceSheetForm";
import FisPanel from "./FisPanel";
import SwissSkiPanel from "./SwissSkiPanel";
import PopulairePanel from "./PopulairePanel";
import RaceDataTransfer from "./RaceDataTransfer";
import { RACE_TYPES, defaultsForType, inferRaceType, deriveSeason } from "./raceTypes";

// Page unique d'export, un frontend dédié par type de course.
// Remplace TimingSwissSkiExport.jsx ; la config reste stockée au même
// endroit (/api/timing/swissski-config) avec 3 champs en plus :
// raceType, sector, seasonManual.

const INITIAL_CONFIG = {
    raceType: null,
    sector: "AL",
    codex: "", date: "", event: "", place: "", timing: "", dataprocessing: "321-timing.ch",
    season: "", seasonManual: false, nation: "SUI", gender: "", discipline: "",
    rtyp: "", fisCategory: "", category: "",
    plist: "", appPenaltyW: "", appPenaltyM: "",
    usedFisList: "", fvalue: "", fisCalculatedPenalty: "", fisAppliedPenalty: "",
    ...EMPTY_SHEET,
};

const PANELS = { fis: FisPanel, swissski: SwissSkiPanel, populaire: PopulairePanel };

export default function TimingRaceExport() {
    const { eventKey } = useParams();
    const navigate = useNavigate();
    // ?next=import : on arrive depuis "Créer / importer" du hub.
    const [searchParams] = useSearchParams();
    const fromCreate = searchParams.get("next") === "import";
    const [config, setConfig] = useState(INITIAL_CONFIG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");

    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            });
            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }
            return res;
        },
        [navigate]
    );

    useEffect(() => {
        setLoading(true);
        apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((cfg) => {
                if (cfg) {
                    setConfig((prev) => ({
                        ...prev,
                        ...cfg,
                        raceType: inferRaceType(cfg),
                        sector: cfg.sector || "AL",
                        // Anciennes configs : une saison différente de celle
                        // déduite de la date a forcément été saisie à la main.
                        seasonManual: cfg.seasonManual ?? (!!cfg.season && cfg.season !== deriveSeason(cfg.date)),
                    }));
                }
            })
            .finally(() => setLoading(false));
    }, [eventKey, apiFetch]);

    // Efface le message de succès après quelques secondes.
    useEffect(() => {
        if (!status) return undefined;
        const t = setTimeout(() => setStatus(""), 4000);
        return () => clearTimeout(t);
    }, [status]);

    // Retourne true si l'enregistrement a réussi (les panneaux s'arrêtent sinon).
    const saveConfig = useCallback(async () => {
        setError("");
        const res = await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(config),
        });
        if (!res) return false;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(`Enregistrement impossible : ${data.error || `HTTP ${res.status}`}`);
            return false;
        }
        return true;
    }, [apiFetch, eventKey, config]);

    // Le choix du type est enregistré tout de suite, pour qu'il soit
    // conservé même si on part directement vers l'import des concurrents.
    const chooseType = async (raceType, sector) => {
        const next = { ...config, ...defaultsForType(raceType), sector: sector || "AL" };
        setConfig(next);
        const res = await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(next),
        });
        if (res && !res.ok) setError(`Enregistrement impossible : HTTP ${res.status}`);
    };

    const goToImport = async () => {
        if (await saveConfig()) navigate(`/timingsoftware/athletes-import/${encodeURIComponent(eventKey)}`);
    };

    // Changer de type garde toutes les données saisies : les champs
    // masqués pour le nouveau type sont simplement ignorés à l'export.
    const changeType = (raceType) => {
        if (raceType === config.raceType) return;
        const label = RACE_TYPES.find((t) => t.value === raceType)?.label;
        if (config.codex && !window.confirm(`Passer cette course en « ${label} » ? Les données saisies sont conservées.`)) return;
        setConfig((prev) => ({ ...prev, raceType }));
    };

    if (loading) {
        return <Box p={3}><CircularProgress size={24} /></Box>;
    }

    const Panel = PANELS[config.raceType];
    const ctx = { config, setConfig, eventKey, apiFetch, saveConfig, setError, setStatus };
    const typeLabel = RACE_TYPES.find((t) => t.value === config.raceType)?.label;

    return (
        <Box p={3} maxWidth={1000}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 3, gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h4">
                    {typeLabel ? `Export ${typeLabel}` : "Nouvelle course"} — {eventKey}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button onClick={() => navigate("/timingsoftware")}>Retour</Button>
                    {/* Export / reprise des données (doubles courses FIS…) —
                        disponible aussi à la création, avant le choix du type. */}
                    <RaceDataTransfer
                        eventKey={eventKey}
                        config={config}
                        setConfig={setConfig}
                        apiFetch={apiFetch}
                        onMessage={(type, msg) => (type === "error" ? setError(msg) : setStatus(msg))}
                    />
                    {config.raceType && (
                        <Button variant="outlined" onClick={async () => (await saveConfig()) && setStatus("Configuration enregistrée")}>
                            Enregistrer
                        </Button>
                    )}
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
            {status && <Alert severity="success" sx={{ mb: 2 }}>{status}</Alert>}

            {fromCreate && config.raceType && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={<Button color="inherit" size="small" onClick={goToImport}>Importer les concurrents</Button>}
                >
                    Complétez le Raceheader, puis passez à l'import des concurrents. Tout reste modifiable plus tard.
                </Alert>
            )}

            {!config.raceType ? (
                <RaceTypeChooser onChoose={chooseType} initialSector={searchParams.get("sector") || "AL"} />
            ) : (
                <>
                    <RaceHeaderForm config={config} setConfig={setConfig} onTypeChange={changeType} />
                    <RaceSheetForm config={config} setConfig={setConfig} />
                    <Panel {...ctx} />
                </>
            )}
        </Box>
    );
}